import { useState, useMemo, useEffect, useContext } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useTranslation } from 'react-i18next'
import { debounce } from 'lodash'
import { PencilIcon, ClockIcon, BanIcon, CheckCircleIcon } from '@heroicons/react/outline'
import { UserContext } from '../../../hooks/UserContext'
import { isSuperAdminUser, isWarehouseAdminUser } from '../../../utils/userUtils'
import { BasicTable, Button, ConfirmDialog, Notification, PageView, PageTopBar } from '../../../components'
import { Loader } from '../../../components/Loader'
import { fetchWarehouse } from '../../../services/warehouseSevices'
import { fetchSalesChannels, createSalesChannel, updateSalesChannel, disableSalesChannel, restoreSalesChannel } from '../../../services'
import { SalesChannelFormModal } from './SalesChannelFormModal'
import { SalesChannelHistoryModal } from './SalesChannelHistoryModal'
import { SCOPE_TYPES, mapFieldError, getErrorMessage } from './utils'

const T_PREFIX = 'sales_channels'
const MAX_VISIBLE_LINKS = 2

const channelLinks = channel => (channel.scope_type === SCOPE_TYPES.WAREHOUSES ? channel.warehouses : channel.stores) || []

export const ListSalesChannelsContainer = () => {
  const { i18n } = useTranslation()
  const { warehouse_id } = useParams()
  const queryClient = useQueryClient()
  const { user } = useContext(UserContext)

  const isAdmin = isSuperAdminUser(user) || isWarehouseAdminUser(user)
  const hasAccess = isAdmin || !!user?.permissions?.index_sales_channels
  const canManage = isAdmin || !!user?.permissions?.manage_sales_channels

  const [filters, setFilters] = useState({ q: '', status: 'active', page: 1 })
  const [qInput, setQInput] = useState('')
  const [formModal, setFormModal] = useState({ open: false, channel: null })
  const [historyModal, setHistoryModal] = useState({ open: false, channel: null })
  const [confirmToggle, setConfirmToggle] = useState({ open: false, channel: null })
  const [notification, setNotification] = useState({ show: false, type: '', title: '' })
  const [serverFieldError, setServerFieldError] = useState(null)

  const showNotification = (type, title) => setNotification({ show: true, type, title })

  const debouncedSetQuery = useMemo(() => debounce(value => setFilters(prev => ({ ...prev, q: value, page: 1 })), 300), [])
  useEffect(() => () => debouncedSetQuery.cancel(), [debouncedSetQuery])

  const handleSearchChange = e => {
    setQInput(e.target.value)
    debouncedSetQuery(e.target.value)
  }
  const handleStatusChange = e => setFilters(prev => ({ ...prev, status: e.target.value, page: 1 }))

  const { data: warehouse } = useQuery(['warehouse', warehouse_id], () => fetchWarehouse(warehouse_id), {
    enabled: hasAccess && !!warehouse_id,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  const searchParams = useMemo(
    () => ({ status: filters.status, q: filters.q || undefined, page: filters.page, per_page: 20 }),
    [filters],
  )

  const { data, isLoading, isFetching } = useQuery(['sales_channels', searchParams], () => fetchSalesChannels(searchParams), {
    enabled: hasAccess,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  })

  const channels = data?.sales_channels || []

  const toastError = error => showNotification('error', getErrorMessage(error) || i18n.t(`${T_PREFIX}.notifications.error`))

  // Field errors are routed to the form modal; actions without a form (disable/restore) use toastError.
  const handleMutationError = error => {
    const fieldErr = mapFieldError(error)
    if (fieldErr) {
      setServerFieldError(fieldErr)
      return
    }
    toastError(error)
  }

  const onWriteSuccess = messageKey => {
    queryClient.invalidateQueries(['sales_channels'])
    setFormModal({ open: false, channel: null })
    setServerFieldError(null)
    showNotification('success', i18n.t(`${T_PREFIX}.notifications.${messageKey}`))
  }

  const createMutation = useMutation(createSalesChannel, {
    onSuccess: () => onWriteSuccess('created'),
    onError: handleMutationError,
  })

  const updateMutation = useMutation(({ id, data: payload }) => updateSalesChannel(id, payload), {
    onSuccess: () => onWriteSuccess('updated'),
    onError: handleMutationError,
  })

  // Reset to page 1: a toggle can shrink the filtered set and empty the current page.
  const afterToggle = messageKey => () => {
    queryClient.invalidateQueries(['sales_channels'])
    setFilters(prev => ({ ...prev, page: 1 }))
    showNotification('success', i18n.t(`${T_PREFIX}.notifications.${messageKey}`))
  }

  const disableMutation = useMutation(id => disableSalesChannel(id), {
    onSuccess: afterToggle('disabled'),
    onError: toastError,
  })

  const restoreMutation = useMutation(id => restoreSalesChannel(id), {
    onSuccess: afterToggle('restored'),
    onError: toastError,
  })

  const handleSubmitForm = payload => {
    if (formModal.channel) updateMutation.mutate({ id: formModal.channel.id, data: payload })
    else createMutation.mutate(payload)
  }

  const handleOpenCreate = () => {
    setServerFieldError(null)
    setFormModal({ open: true, channel: null })
  }

  const handleOpenEdit = channel => {
    setServerFieldError(null)
    setFormModal({ open: true, channel })
  }

  const handleConfirmToggle = () => {
    const channel = confirmToggle.channel
    if (!channel) return
    const mutation = channel.active ? disableMutation : restoreMutation
    mutation.mutate(channel.id, { onSettled: () => setConfirmToggle(prev => ({ ...prev, open: false })) })
  }

  const columns = useMemo(
    () => [
      { Header: i18n.t(`${T_PREFIX}.headers.name`), accessor: 'name', disableSortBy: true },
      { Header: i18n.t(`${T_PREFIX}.headers.scope`), accessor: 'scope', disableSortBy: true },
      { Header: i18n.t(`${T_PREFIX}.headers.links`), accessor: 'links', disableSortBy: true },
      { Header: i18n.t(`${T_PREFIX}.headers.status`), accessor: 'status', disableSortBy: true },
      { Header: i18n.t(`${T_PREFIX}.headers.actions`), accessor: 'actions', disableSortBy: true },
    ],
    [i18n],
  )

  const tableData = useMemo(
    () =>
      channels.map(channel => {
        const links = channelLinks(channel)
        const names = links.map(l => l.name)
        const shown = names.slice(0, MAX_VISIBLE_LINKS).join(', ')
        const extra = names.length - MAX_VISIBLE_LINKS
        return {
          name: channel.name,
          scope: (
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${channel.scope_type === SCOPE_TYPES.WAREHOUSES ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}
            >
              {i18n.t(`${T_PREFIX}.scope_types.${channel.scope_type}`)}
            </span>
          ),
          links: (
            <span className="text-sm text-gray-700">
              {shown}
              {extra > 0 ? ` (+${extra})` : ''}
            </span>
          ),
          status: (
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${channel.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
            >
              {channel.active ? i18n.t(`${T_PREFIX}.status_active`) : i18n.t(`${T_PREFIX}.status_inactive`)}
            </span>
          ),
          actions: (
            <div className="flex space-x-2">
              <button
                onClick={() => setHistoryModal({ open: true, channel })}
                className="p-1 text-gray-500 hover:text-blue-600 transition-colors"
                title={i18n.t(`${T_PREFIX}.actions.history`)}
                aria-label={i18n.t(`${T_PREFIX}.actions.history`)}
              >
                <ClockIcon className="h-5 w-5" />
              </button>
              {canManage && (
                <>
                  {channel.active && (
                    <button
                      onClick={() => handleOpenEdit(channel)}
                      className="p-1 text-gray-500 hover:text-indigo-600 transition-colors"
                      title={i18n.t(`${T_PREFIX}.actions.edit`)}
                      aria-label={i18n.t(`${T_PREFIX}.actions.edit`)}
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                  )}
                  <button
                    onClick={() => setConfirmToggle({ open: true, channel })}
                    className={`p-1 text-gray-500 transition-colors ${channel.active ? 'hover:text-red-600' : 'hover:text-green-600'}`}
                    title={channel.active ? i18n.t(`${T_PREFIX}.actions.disable`) : i18n.t(`${T_PREFIX}.actions.restore`)}
                    aria-label={channel.active ? i18n.t(`${T_PREFIX}.actions.disable`) : i18n.t(`${T_PREFIX}.actions.restore`)}
                  >
                    {channel.active ? <BanIcon className="h-5 w-5" /> : <CheckCircleIcon className="h-5 w-5" />}
                  </button>
                </>
              )}
            </div>
          ),
        }
      }),
    [channels, canManage, i18n],
  )

  if (!hasAccess) {
    return (
      <PageView
        topMenu={
          <PageTopBar>
            <div className="text-lg font-semibold">{i18n.t(`${T_PREFIX}.title`)}</div>
          </PageTopBar>
        }
      >
        <div className="text-center py-12 text-gray-500">{i18n.t(`${T_PREFIX}.errors.no_access`)}</div>
      </PageView>
    )
  }

  return (
    <PageView
      topMenu={
        <PageTopBar>
          <div className="text-lg font-semibold">{i18n.t(`${T_PREFIX}.title`)}</div>
          <div className="flex items-center gap-2 ml-auto">
            <input
              type="text"
              value={qInput}
              onChange={handleSearchChange}
              placeholder={i18n.t(`${T_PREFIX}.filters.search`)}
              aria-label={i18n.t(`${T_PREFIX}.filters.search`)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <select
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={filters.status}
              onChange={handleStatusChange}
              aria-label={i18n.t(`${T_PREFIX}.filters.status_label`)}
            >
              <option value="active">{i18n.t(`${T_PREFIX}.filters.status_active`)}</option>
              <option value="disabled">{i18n.t(`${T_PREFIX}.filters.status_disabled`)}</option>
              <option value="all">{i18n.t(`${T_PREFIX}.filters.status_all`)}</option>
            </select>
            {canManage && (
              <Button type="primary" onClick={handleOpenCreate}>
                {i18n.t(`${T_PREFIX}.actions.create`)}
              </Button>
            )}
          </div>
        </PageTopBar>
      }
      childrenFullWidth={true}
      topMenuFullWidth={true}
    >
      <Notification
        show={notification.show}
        setShow={val => setNotification(prev => ({ ...prev, show: val }))}
        type={notification.type}
        title={notification.title}
      />

      {isLoading ? (
        <Loader show={true} />
      ) : (
        <BasicTable
          columns={columns}
          data={tableData}
          showHeader={true}
          showLoader={isFetching}
          isFetching={isFetching}
          emptyTableText={i18n.t(`${T_PREFIX}.no_channels`)}
          showPaginator={true}
          paginationMeta={data?.meta}
          onPaginationChange={page => setFilters(prev => ({ ...prev, page }))}
        />
      )}

      <SalesChannelFormModal
        open={formModal.open}
        setOpen={val => setFormModal(prev => ({ ...prev, open: val }))}
        selectedChannel={formModal.channel}
        onSubmit={handleSubmitForm}
        isLoading={createMutation.isLoading || updateMutation.isLoading}
        serverFieldError={serverFieldError}
        onClearFieldError={() => setServerFieldError(null)}
      />

      <SalesChannelHistoryModal
        open={historyModal.open}
        setOpen={val => setHistoryModal(prev => ({ ...prev, open: val }))}
        channel={historyModal.channel}
        warehouseTimeZone={warehouse?.time_zone}
      />

      <ConfirmDialog
        open={confirmToggle.open}
        setOpen={val => setConfirmToggle(prev => ({ ...prev, open: val }))}
        title={confirmToggle.channel?.active ? i18n.t(`${T_PREFIX}.actions.disable`) : i18n.t(`${T_PREFIX}.actions.restore`)}
        description={confirmToggle.channel?.active ? i18n.t(`${T_PREFIX}.actions.confirm_disable`) : i18n.t(`${T_PREFIX}.actions.confirm_restore`)}
        confirmLabel={confirmToggle.channel?.active ? i18n.t(`${T_PREFIX}.actions.disable`) : i18n.t(`${T_PREFIX}.actions.restore`)}
        cancelLabel={i18n.t(`${T_PREFIX}.form.cancel`)}
        onConfirm={handleConfirmToggle}
        onCancel={() => setConfirmToggle(prev => ({ ...prev, open: false }))}
        loading={disableMutation.isLoading || restoreMutation.isLoading}
        type={confirmToggle.channel?.active ? 'danger' : 'default'}
      />
    </PageView>
  )
}
